import React, { useContext } from "react";
import { ThemeProvider as MuiThemeProvider, CssBaseline } from "@mui/material";
import MainRouter from "./components/Routers/Router";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { ThemeProvider, ThemeContext } from "./ThemeContext";
import { lightTheme, darkTheme } from "./theme";
import ErrorBoundary from "./components/ErrorBoundary.jsx";
import { getBxiApiKey } from "./config/apiKey";


// API base URL — env-driven (REACT_APP_API_URL from .env.development /
// .env.production / .env.local, selected by npm run start:dev|start:prod|start:local).
// Falls back to the development API so a missing/blank env never silently points
// the admin panel at localhost. Normalizes trailing slashes then appends one.
axios.defaults.baseURL =
  (process.env.REACT_APP_API_URL || "https://apiv1production.bxiworld.com").replace(/\/+$/, "") + "/";

// Public app identifier sent on every request (NOT a secret — bundled client-side).
// Fail-closed: no hardcoded fallback (see config/apiKey.js).
axios.defaults.headers.bxiapikey = getBxiApiKey();

// SECURITY (VAPT-2 Finding 2): attach the admin Bearer token to every API request
// so admin endpoints can enforce auth (some callers previously sent no token and
// relied on routes being open). Scoped to same-origin / API-base requests so the
// token is never leaked to third-party origins (e.g. S3). Individual callers that
// already set Authorization are left as-is.
axios.interceptors.request.use((config) => {
  try {
    const url = config.url || "";
    const isAbsolute = /^https?:\/\//i.test(url);
    const apiBase = axios.defaults.baseURL || "";
    const targetsApi = !isAbsolute || (apiBase && url.startsWith(apiBase));
    if (targetsApi && !config.headers?.Authorization) {
      const match = document.cookie
        .split("; ")
        .find((c) => c.startsWith("BearerToken="));
      const token = match ? match.substring("BearerToken=".length) : null;
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  } catch (_) {
    // Never block a request because the interceptor failed.
  }
  return config;
});

function AppWithMuiTheme() {
  const { darkMode } = useContext(ThemeContext);
  const theme = darkMode ? darkTheme : lightTheme;
  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <ToastContainer />
      <MainRouter />
    </MuiThemeProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AppWithMuiTheme />
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
